import { userTypes } from 'bmiq-common';

// Trusted athenaOne origins allowed to post embedded-app messages, kept in sync
// with the server's CSP `frame-ancestors` entry (see helmet.js). The `*.` prefix
// is a subdomain wildcard covering preview.athenahealth.com (preview) and
// athenanet.athenahealth.com (production).
export const ATHENA_EMBED_ORIGINS = 'https://*.athenahealth.com';

// athenaOne broadcasts this event to the embedded app whenever the active
// patient changes (see athenahealth "Embedded Apps" messaging framework docs).
export const PATIENT_CONTEXT_CHANGED = 'patientContextChanged';

export type PatientContextChangedEvent = {
  event: typeof PATIENT_CONTEXT_CHANGED;
  // athenaOne patient id, or undefined when patient context is cleared.
  updatedPatient?: string;
};

const parseAllowlist = (raw: string): string[] =>
  raw
    .split(/\s+/)
    .map(origin => origin.trim())
    .filter(Boolean);

// Matches an origin against a single allowlist entry that may contain a leading
// `*.` subdomain wildcard, e.g. `https://*.athenahealth.com` matches
// `https://preview.athenahealth.com` but not `https://evil-athenahealth.com`.
const WILDCARD = '://*.';
const originMatchesPattern = (origin: string, pattern: string): boolean => {
  const wildcardIndex = pattern.indexOf(WILDCARD);
  // Non-wildcard entries (and any that happen to equal the origin) match exactly.
  if (wildcardIndex === -1) {
    return pattern === origin;
  }
  const scheme = pattern.slice(0, wildcardIndex + 3); // keep the trailing "://"
  const domain = pattern.slice(wildcardIndex + WILDCARD.length);
  return origin.startsWith(scheme) && origin.endsWith(`.${domain}`);
};

// Whether a postMessage `event.origin` is a trusted athenaOne embed origin.
export const isAllowedAthenaOrigin = (origin: string, allowlist: string = ATHENA_EMBED_ORIGINS): boolean => {
  if (!origin) {
    return false;
  }
  return parseAllowlist(allowlist).some(pattern => originMatchesPattern(origin, pattern));
};

// Narrows an untrusted postMessage payload to the athenaOne
// `patientContextChanged` event, returning null for anything else.
const isPatientContextChanged = (data: unknown): data is { event: unknown; updatedPatient?: unknown } =>
  typeof data === 'object' && data !== null && (data as { event?: unknown }).event === PATIENT_CONTEXT_CHANGED;

export const parsePatientContextEvent = (data: unknown): PatientContextChangedEvent | null => {
  if (!isPatientContextChanged(data)) {
    return null;
  }
  return {
    event: PATIENT_CONTEXT_CHANGED,
    ...(typeof data.updatedPatient === 'string' ? { updatedPatient: data.updatedPatient } : {}),
  };
};

// The patient page an athenaOne launch, or an in-iframe patient switch, should land on. `asAdmin`
// puts the page in its admin view, the same URL the admin patients list links to. Kept in step with
// the server's SSO redirect in `ssoCallbackRoute`.
export const athenaPatientRoute = (patientId: number, userType?: string): string =>
  `/patient/?patientId=${patientId}${userType === userTypes.USER_TYPE_ADMIN ? '&asAdmin=true' : ''}`;

// An athena sidecar launch is marked on its URL, not in the session, so a normal tab signed in as the same user is
// unaffected. Kept in step with the server's sidecar redirects in `ssoCallbackRoute`.
const SIDECAR_PARAMS = 'embed=athena';

// The patient page `view` that opens the personalized care plan.
export const CARE_PLAN_VIEW = 'carePlan';

// Whether the current URL belongs to an athena sidecar launch.
export const isAthenaSidecarLaunch = (search: string): boolean => new URLSearchParams(search).get('embed') === 'athena';

// The care plan a sidecar launch, or an in-sidecar patient switch, should land on.
export const athenaSidecarCarePlanRoute = (patientId: number, userType?: string): string =>
  `${athenaPatientRoute(patientId, userType)}&view=${CARE_PLAN_VIEW}&${SIDECAR_PARAMS}`;

// Where the sidecar lands when athena's patient has no FlyteHealth record the user may open.
export const ATHENA_SIDECAR_PATIENT_NOT_FOUND_ROUTE = `/patients?${SIDECAR_PARAMS}&athenaPatientNotFound=true`;
